import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { useFormik } from "formik";
import * as yup from "yup";
import { CircularProgress, TextField } from "@mui/material";
import { useNavigate } from "react-router-dom";
import {
    getMasterUserProfileDetailAction,
    updateMasterUserProfileAction,
} from "../../Actions/ProfileActions";
import CustomSnackbar from "../Common/snackBar";
import { styled } from "@mui/system";
import {
    API_IMAGE_URL,
    CRYPTO_TOKEN_SECRET_KEY,
} from "../../config";
import CryptoJS from "crypto-js";
import { useUser } from "../../components/ContextAPI/ProfileContext";
import { getUserData } from "../../Actions/common";
import { SecureIcon } from "../../Assets/svg/svgicon";
import { enableTwoFactorAuth } from "../../Actions/AuthActions";
import ChangePasswordComponent from "./ChangePassword_index";
import BreadcrumbsComponent from "../../components/Common/Breadcrumbs";
import ProfilePictureUpload from "../../components/Common/ProfilePictureUpload";
import showConfirmation from "../../utils/Conformation";
import { FRONTEND_MESSAGES } from "../../utils/messages";
import { MyLockIcon } from "../../Assets/svg/MyIcons";

const { VALIDATION } = FRONTEND_MESSAGES;

const getAuthToken = () => {
    const decryptedData = CryptoJS.AES.decrypt(
        localStorage.getItem("token"),
        CRYPTO_TOKEN_SECRET_KEY
    ).toString(CryptoJS.enc.Utf8);
    return decryptedData;
};

const validationSchema = yup.object().shape({
    firstName: yup
        .string()
        .trim()
        .required(VALIDATION.REQUIRED_FIELD("First name"))
        .min(1, VALIDATION.MIN_LENGTH("First name", 1))
        .max(50, VALIDATION.MAX_LENGTH("First name", 50))
        .matches(/^[a-zA-Z_\-'\u2018\u2019\u2018\u0060\u2013\u2014]+$/, VALIDATION.INVALID_FORMAT("First name")),
    lastName: yup
        .string()
        .trim()
        .required(VALIDATION.REQUIRED_FIELD("Last name"))
        .min(1, VALIDATION.MIN_LENGTH("Last name", 1))
        .max(50, VALIDATION.MAX_LENGTH("Last name", 50))
        .matches(/^[a-zA-Z_\-'\u2018\u2019\u2018\u0060\u2013\u2014]+$/, VALIDATION.INVALID_FORMAT("Last name")),
    profilePic: yup
        .mixed()
        .nullable()
        .test(
            "fileType",
            VALIDATION.FILE_TYPE_INVALID(['jpeg', 'png', 'jpg']),
            (value) => {
                if (!value) {
                    return true; // Allow null/empty (user can remove picture)
                }

                const isFile = value instanceof File;
                if (!isFile) {
                    return true; // Allow existing string URLs
                }

                const supportedFormats = ["image/jpeg", "image/png", "image/jpg"];
                return supportedFormats.includes(value.type);
            },
        )
        .test("fileSize", VALIDATION.FILE_SIZE_MAX("2 MB", "Profile picture"), (value) => {
            const maxFileSize = 2 * 1024 * 1024; // 2 MB
            if (!value) {
                return true;
            }

            const isFile = value instanceof File;
            if (!isFile) {
                return true;
            }

            return value.size <= maxFileSize;
        }),
});

const ChangeMasterUserProfile = () => {
    const location = useLocation();
    const { state } = location;
    
    const [openModal, setOpenModal] = useState(state?.openModel || false);
    const [userData, setUserData] = useState(null);
    const [isImageEdited, setIsImageEdited] = useState(false);
    const [loading, setLoading] = useState(false);
    const [spinner, setSpinner] = useState(false);
    const [buttonDisable, setButtonDisable] = useState(false);
    const { updateUserData } = useUser();
    const [snackbar, setSnackbar] = useState({
        open: false,
        message: "",
        severity: "success",
    });
    const navigate = useNavigate();
    const authToken = getAuthToken();
    const CircleContainer = styled("div")`
        display: flex;
        justify-content: center;
        align-items: center;
        width: 100px; // Set your desired width for the circle
        height: 100px; // Set your desired height for the circle
        position: relative;
        animation: rotateCircle 4s linear infinite;
    `;


    const CircularIndeterminate = () => {
        return (
            <div
                style={{
                    display: loading ? "flex" : "none",
                    position: "fixed",
                    top: 0,
                    left: 0,
                    width: "100%",
                    height: "100%",
                    backgroundColor: "rgba(255, 255, 255, 0.7)",
                    zIndex: 9999,
                    justifyContent: "center",
                    alignItems: "center",
                }}
            >
                <CircleContainer>
                    <CircularProgress
                        color="primary"
                        size={24}
                        style={{
                            width: "50px",
                            height: "50px",
                        }}
                    />
                </CircleContainer>
            </div>
        );
    };

    useEffect(() => {
        if (!authToken) {
            setSnackbar({
                open: true,
                message: "Invalid token! Please login again",
                severity: "error",
            });
            setTimeout(() => {
                navigate("/login");
            }, 1500);
        }
        getUserProfileDetails();
    }, []); 

    const getUserProfileDetails = async () => {
        setLoading(true);
        await getMasterUserProfileDetailAction()
            .then((res) => {
                setUserData(res?.data?.data);
                updateUserData(res?.data?.data);
                setLoading(false);
                if (res?.data) {
                    let userData = localStorage.getItem("userData")
                        ? getUserData()
                        : null;
                    userData = {
                        ...userData,
                        firstName: res.data.data.firstName,
                        lastName: res.data.data.lastName,
                        profilePic: res.data.data.profilePic,
                    };
                    const encryptedDateFormat = CryptoJS.AES.encrypt(
                        JSON.stringify(userData),
                        CRYPTO_TOKEN_SECRET_KEY
                    ).toString();
                    localStorage.setItem("userData", encryptedDateFormat);
                }
            })
            .catch((error) => {
                setSnackbar({
                    open: true,
                    message: error?.response?.data?.message,
                    severity: "error",
                });
            });
        setLoading(false);
    };

    const handleTwoFactorToggle = async () => {
        const isEnabling = !formik.values.twoFactor;

        await showConfirmation("toggleStatus", {
            entityName: "Two-Factor Authentication",
            entityId: null,
            currentStatus: formik.values.twoFactor,
            action: async () => {
                const data = {
                    twoFactor: isEnabling ? "Enable" : "Disable",
                };
                return await enableTwoFactorAuth(data);
            },
            onSuccess: (res, newStatus) => {
                setSnackbar({
                    open: true,
                    message: res?.data?.message || `Two-Factor Authentication ${newStatus ? "enabled" : "disabled"} successfully.`,
                    severity: res?.data?.status === 200 ? "success" : "info",
                });
                getUserProfileDetails();
            },
            onError: (error) => {
                setSnackbar({
                    open: true,
                    message: error?.message || "Something went wrong",
                    severity: "error",
                });
            },
        });
    };

    // const handleCloseModal = () => {
    //     // setState((prevState) => ({ ...prevState, openModal: "" }));
    //     setOpenModal(false);
    // };

    const handleCloseModal = () => {
        setOpenModal(false);
        // ChangeUserPassword(false);
        // Update state by navigating to same page with new state
        navigate(location.pathname, {
            replace: true, // optional, prevents adding a new history entry
            state: { ...location.state, openModel: false },
        });
    };

    const handleCloseSnackbar = () => {
        setSnackbar({ ...snackbar, open: false });
    };
    const formik = useFormik({
        enableReinitialize: true,
        initialValues: {
            firstName: userData?.firstName ? userData.firstName : "",
            lastName: userData?.lastName ? userData.lastName : "",
            profilePic: userData?.profilePic ? userData.profilePic : null,
            profilePicPreview: userData?.profilePic
                ? `${API_IMAGE_URL}/${userData.profilePic}`
                : "",
            twoFactor: userData?.twoFactor ? userData?.twoFactor : false,
        },
        validationSchema: validationSchema,
        onSubmit: async (values) => {
            setButtonDisable(true);
            setSpinner(true);
            const formData = new FormData();
            formData.append("firstName", values.firstName);
            formData.append("lastName", values.lastName);
            formData.append("isImageEdited", isImageEdited);
            // formData.append("mediaUpload",true);
            formData.append("filename", userData.profilePic);
            formData.append("twoFactor", values.twoFactor);

            if (values.profilePic) {
                formData.append("profilePic", values.profilePic);
            }
            await updateMasterUserProfileAction(formData)
                .then((res) => {
                    if (res.status === "202") {
                        setSnackbar({
                            open: true,
                            message: res.data.message,
                            severity: "info",
                        });
                    } else {
                        // if (res.status == "200") {
                        setSnackbar({
                            open: true,
                            message: res.data.message,
                            severity: "success",
                        });
                        // }
                        getUserProfileDetails();
                    }
                })
                .catch((error) => {
                    // console.log(error.error,typeof error.error)
                    const message = error.message ?? error.error
                    setSnackbar({
                        open: true,
                        message,
                        severity: "error",
                    });
                })
                .finally(() => {
                    setSpinner(false);
                    setButtonDisable(false);
                });
        },
    });

    const handleNameChange = (e) => {
        formik.setFieldValue(e.target.name, e.target.value.replace(/\s/g, ''));
        formik.setFieldTouched(e.target.name, true, false);
    };

    const handleFileSelect = (file) => {
        if (userData) {
            setIsImageEdited(true);
        }
        formik.setFieldValue("profilePic", file);

        // Preview the selected image
        if (file) {
            const reader = new FileReader();
            reader.onload = (e) => {
                formik.setFieldValue("profilePicPreview", e.target.result);
            };
            reader.readAsDataURL(file);
        }
    };

    const handleRemovePicture = () => {
        setIsImageEdited(true);
        formik.setFieldValue("profilePic", null);
        formik.setFieldValue("profilePicPreview", "");
    };

    return (
        <div id="kt_content_container">
            {loading && <CircularIndeterminate />}
            <BreadcrumbsComponent />
            {/* <div className="card card-body ">
                <div className="d-flex align-items-center justify-space-between p-3"> */}
                    {/* <div className="page-title">
                        <h1 className=" fw-bold fs-2 py-3 pt-10 mb-10">Profile Details</h1>
                    </div> */}
                    {/* <div className="ms-auto">
                        <button
                            type="button"
                            className="btn btb-sm rounded-3 btn-sm btn-light-primary py-1"
                        >
                            {" "}
                            Back{" "}
                        </button>
                    </div>
                </div>
            </div> */}

            <div className="card mb-5 mb-xl-10">
                <div
                    id="kt_account_settings_profile_details"
                    className="collapse show"
                >
                    <form className="form" onSubmit={formik.handleSubmit}>
                        <div className="card-body border-top p-9">
                            <div className="row mb-6">
                                <div className="col-lg-12">
                                    <ProfilePictureUpload
                                        onFileSelect={handleFileSelect}
                                        onRemove={handleRemovePicture}
                                        accept="image/jpeg,image/png,image/jpg"
                                        maxSize={2 * 1024 * 1024} // 2MB
                                        error={
                                            formik.touched.profilePic && formik.errors.profilePic
                                                ? formik.errors.profilePic
                                                : null
                                        }
                                        preview={formik.values.profilePicPreview}
                                        firstName={formik.values.firstName || userData?.firstName || ""}
                                        lastName={formik.values.lastName || userData?.lastName || ""}
                                        size={120}
                                    />
                                </div>

                                <div className="col-lg-4 col-md-6 fv-row">
                                    <label className="form-label mt-4">
                                        First Name <span className="text-danger ms-1">*</span>
                                    </label>
                                    <TextField
                                        placeholder="Enter first name"
                                        name="firstName"
                                        className="mt-0"
                                        size="small"
                                        type="text"
                                        value={formik.values.firstName}
                                        onChange={handleNameChange}
                                        onBlur={formik.handleBlur}
                                        error={
                                            formik.touched.firstName &&
                                            Boolean(formik.errors.firstName)
                                        }
                                        helperText={
                                            formik.touched.firstName &&
                                            formik.errors.firstName
                                        }
                                        fullWidth
                                        margin="normal"
                                    />
                                </div>
                                <div className="col-lg-4 col-md-6 fv-row">
                                    <label className="form-label mt-4">
                                        Last Name <span className="text-danger ms-1">*</span>
                                    </label>
                                    <TextField
                                        placeholder="Enter last name"
                                        name="lastName"
                                        className="mt-0"
                                        size="small"
                                        type="text"
                                        value={formik.values.lastName}
                                        onChange={handleNameChange}
                                        onBlur={formik.handleBlur}
                                        error={
                                            formik.touched.lastName &&
                                            Boolean(formik.errors.lastName)
                                        }
                                        helperText={
                                            formik.touched.lastName &&
                                            formik.errors.lastName
                                        }
                                        fullWidth
                                        margin="normal"
                                    />
                                </div>
                                <div className=" col-lg-4 col-md-12  fv-row">
                                    <label className="form-label mt-4">
                                        Email
                                    </label>
                                    <TextField
                                        className="mt-0"
                                        size="small"
                                        name="email"
                                        value={
                                            userData?.email
                                                ? userData.email
                                                : ""
                                        }
                                        fullWidth
                                        margin="normal"
                                        disabled
                                    />
                                </div>
                            </div>
                        </div>
                        <div className="card-footer d-flex justify-content-end py-6 px-9">
                            <button
                                type="submit"
                                className="btn btn-primary btn-sm"
                                disabled={buttonDisable || spinner  || !formik.isValid}
                            >
                                Save Changes
                                {spinner ? (
                                    <span
                                        className="spinner-border spinner-border-sm ms-2"
                                        role="status"
                                        // aria-hidden="true"
                                    ></span>
                                ) : null}
                            </button>
                        </div>
                    </form>
                </div>
            </div>

            <div className="card mb-5 mb-xl-10">
                <div
                    className="card-header border-0 cursor-pointer"
                    role="button"
                    data-bs-toggle="collapse"
                    data-bs-target="#kt_account_signin_method"
                >
                    <div className="card-title m-0">
                        <h3 className="fw-semibold m-0">Sign-in method</h3>
                    </div>
                </div>

                <div
                    id="kt_account_settings_signin_method"
                    className="collapse show"
                >
                    <div className="card-body border-top p-9">
                        <div className="notice d-flex bg-light-success rounded border-success border border-dashed p-6 mb-10">
                            <i className="me-4"> <MyLockIcon style={{ width: "30px", height: "40px" }} /> </i>

                            <div className="d-flex flex-stack flex-grow-1 flex-wrap flex-md-nowrap">
                                <div className="mb-3 mb-md-0">
                                    <h4 className="fw-semibold">
                                        Password Protection
                                    </h4>
                                    <p className="fs-6 text-gray-700 pe-7 mb-0">
                                        Keep your account secure with a strong password using letters, numbers, and special characters.
                                    </p>
                                </div>

                                <button
                                    className="btn btn-primary px-6 align-self-center text-nowrap"
                                    onClick={() => setOpenModal(true)}
                                >
                                    Change password
                                </button>
                            </div>
                        </div>
                        <ChangePasswordComponent
                            open={openModal}
                            onClose={handleCloseModal}
                            snackbar={snackbar}
                            setSnackbar={setSnackbar}
                        />

                        <div className="notice d-flex bg-light-primary rounded border-primary border border-dashed p-6">
                            <i className="me-4">
                                <SecureIcon width="30" height="30" />
                            </i>

                            <div className="d-flex flex-stack flex-grow-1 flex-wrap flex-md-nowrap">
                                <div className="mb-3 mb-md-0">
                                    <h4 className="fw-semibold">
                                        Secure your account
                                    </h4>
                                    <p className="fs-6 text-gray-700 pe-7 mb-0">
                                        Two-factor authentication adds an extra
                                        layer of security to your account. To
                                        log in, in addition you'll need to
                                        provide a 6 digit code
                                    </p>
                                </div>

                                <span
                                    className="btn btn-primary px-6 align-self-center text-nowrap"
                                    data-bs-toggle="modal"
                                    data-bs-target="#kt_modal_two_factor_authentication"
                                    onClick={handleTwoFactorToggle}
                                >
                                    {formik.values.twoFactor
                                        ? "Disable"
                                        : "Enable"}
                                </span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <CustomSnackbar
                open={snackbar.open}
                message={snackbar.message}
                severity={snackbar.severity}
                onClose={handleCloseSnackbar}
            />
        </div>
    );
};

export default ChangeMasterUserProfile;
